import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CheckCircle2, FileText, Loader2, Sparkles, TriangleAlert } from "lucide-react";
import MarkdownRenderer from "../agno/MarkdownRenderer";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import type { MeetingDoc } from "../../services/meetingDocsEndpoint";
import {
  getMeetingDocLabel,
  getMeetingDocMarkdown,
  normalizeMeetingDocStatus,
} from "../../services/meetingDocsHelpers";

export type DocTab = "prep" | "followup" | "custom";

type MeetingDocTabsProps = {
  prepDoc?: MeetingDoc | null;
  followupDoc?: MeetingDoc | null;
  customDocs?: MeetingDoc[];
  initialTab?: DocTab;
  /** Key that resets the tab when the parent context changes (e.g. meeting id). */
  resetKey?: string;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return format(parsed, "MMM d, yyyy h:mm a");
};

const StatusBadge = ({ status }: { status: ReturnType<typeof normalizeMeetingDocStatus> }) => {
  const label = status === "success" ? "Ready" : status === "failed" ? "Failed" : "In progress";
  return (
    <Badge
      variant="outline"
      className={[
        "gap-1.5 shrink-0",
        status === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : status === "failed"
            ? "border-red-200 bg-red-50 text-red-700"
            : "border-amber-200 bg-amber-50 text-amber-700",
      ].join(" ")}
    >
      {status === "success" ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : status === "failed" ? (
        <TriangleAlert className="h-3.5 w-3.5" />
      ) : (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      )}
      {label}
    </Badge>
  );
};

const DocPane = ({ doc, fallbackLabel }: { doc: MeetingDoc | null | undefined; fallbackLabel: string }) => {
  if (!doc) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <FileText className="h-5 w-5 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No {fallbackLabel.toLowerCase()} doc yet</p>
        <p className="text-xs text-muted-foreground/70">
          Trigger the action from the meetings page to generate one.
        </p>
      </div>
    );
  }

  const status = normalizeMeetingDocStatus(doc.status);
  const markdown = getMeetingDocMarkdown(doc);
  const docLabel = getMeetingDocLabel(doc.doc_type);
  const generatedAt = formatTimestamp(doc.completed_at || doc.created_at);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {generatedAt ? `${docLabel} doc • ${generatedAt}` : `${docLabel} doc`}
        </span>
        <StatusBadge status={status} />
      </div>

      {markdown ? (
        <MarkdownRenderer classname="text-sm leading-6 text-foreground">
          {markdown}
        </MarkdownRenderer>
      ) : status === "failed" ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {doc.error_message || `ORA could not generate this ${docLabel.toLowerCase()} doc.`}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          <div className="mb-2 inline-flex items-center gap-2 text-foreground">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-medium">{docLabel} doc is being generated</span>
          </div>
          <p>ORA is still preparing this doc. Refresh shortly to view the full brief.</p>
        </div>
      )}
    </div>
  );
};

const CustomDocsPane = ({ docs }: { docs: MeetingDoc[] }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <FileText className="h-5 w-5 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No custom docs yet</p>
        <p className="text-xs text-muted-foreground/70">
          Custom docs created by skills will appear here.
        </p>
      </div>
    );
  }

  const doc = docs[selectedIdx] || docs[0];

  return (
    <div>
      {docs.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {docs.map((d, idx) => (
            <button
              key={d.id}
              type="button"
              className={[
                "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
                idx === selectedIdx
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground",
              ].join(" ")}
              onClick={() => setSelectedIdx(idx)}
            >
              {d.title || d.doc_type || `Doc ${idx + 1}`}
            </button>
          ))}
        </div>
      )}
      <DocPane doc={doc} fallbackLabel="Custom" />
    </div>
  );
};

const MeetingDocTabs = ({
  prepDoc,
  followupDoc,
  customDocs = [],
  initialTab,
  resetKey,
}: MeetingDocTabsProps) => {
  const hasPrep = Boolean(prepDoc);
  const hasFollowup = Boolean(followupDoc);
  const hasCustom = customDocs.length > 0;

  const resolveDefaultTab = (): DocTab => {
    if (initialTab) return initialTab;
    if (hasPrep) return "prep";
    if (hasFollowup) return "followup";
    if (hasCustom) return "custom";
    return "prep";
  };

  const [activeTab, setActiveTab] = useState<DocTab>(resolveDefaultTab);

  useEffect(() => {
    setActiveTab(resolveDefaultTab());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, initialTab]);

  // Nothing to show at all
  if (!hasPrep && !hasFollowup && !hasCustom) return null;

  return (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DocTab)}>
      <TabsList className="h-8 w-full bg-tab-bar p-0.5">
        <TabsTrigger value="prep" className="flex-1 gap-1.5 text-xs">
          <Sparkles className="h-3 w-3" />
          Prep
          {hasPrep && normalizeMeetingDocStatus(prepDoc!.status) === "success" && (
            <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
          )}
        </TabsTrigger>
        <TabsTrigger value="followup" className="flex-1 gap-1.5 text-xs">
          <Sparkles className="h-3 w-3" />
          Follow-up
          {hasFollowup && normalizeMeetingDocStatus(followupDoc!.status) === "success" && (
            <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
          )}
        </TabsTrigger>
        <TabsTrigger value="custom" className="flex-1 gap-1.5 text-xs">
          <FileText className="h-3 w-3" />
          Custom
          {hasCustom && (
            <span className="ml-0.5 rounded-full bg-muted-foreground/20 px-1.5 text-[10px] text-muted-foreground">
              {customDocs.length}
            </span>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="prep" className="mt-0">
        <div className="py-4">
          <DocPane doc={prepDoc || null} fallbackLabel="Prep" />
        </div>
      </TabsContent>

      <TabsContent value="followup" className="mt-0">
        <div className="py-4">
          <DocPane doc={followupDoc || null} fallbackLabel="Follow-up" />
        </div>
      </TabsContent>

      <TabsContent value="custom" className="mt-0">
        <div className="py-4">
          <CustomDocsPane docs={customDocs} />
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default MeetingDocTabs;
